import {
  ActionButton,
  formatDate,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useOutputDescriptionProcessingStatesQuery,
  useSystemClassifierItemsBy,
  Form,
  OutputDescriptionProcessingStatesType,
  FormAlerts,
  Detail,
  ValidateOutputDataType,
  OutputDescriptionProcessingStateType,
  ValidateOutputDataSchema,
  useValidateOutputData,
  useMutationErrors,
  useAlertContext,
  GetApiErrorMessage,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { FC } from 'react';
import translations from './OutputDataActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ActiveOutputsTable } from './ActiveOutputsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import { OutputDataActionParams } from '../OutputDataDirectory';

const { addTranslations, exists, t } = i18nNsFactory(
  'output-data-action-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export interface ValidationModalProps {
  params: OutputDataActionParams;
  onClose: () => void;
}

export const ValidationModal: FC<ValidationModalProps> = (props) => {
  const { params, onClose } = props;
  const { hasAccess, ...otherParams } = params;
  const { data, isLoading } =
    useOutputDescriptionProcessingStatesQuery(otherParams);
  const { mutateAsync } = useValidateOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const systemClassifierItemsById = useSystemClassifierItemsBy(['OUTPUT_TYPE']);

  if (!data) {
    return null;
  }

  return (
    <Form
      schema={ValidateOutputDataSchema}
      defaultValues={getDefaultValues(
        data,
        systemClassifierItemsById['OUTPUT_TYPE'],
      )}
      onSubmit={(data: ValidateOutputDataType) => {
        resetAlert();
        clearMutationErrors();
        return mutateAsync({ data })
          .then(() => {
            onClose();
            setSuccess(t('alert.success.validated'));
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <ValidationModalInner
        data={data}
        isLoading={isLoading}
        onClose={onClose}
        hasGroupAccess={hasAccess}
      />
    </Form>
  );
};

const ValidationModalInner: FC<{
  data: OutputDescriptionProcessingStatesType;
  isLoading?: boolean;
  onClose: () => void;
  hasGroupAccess: boolean;
}> = (props) => {
  const { data, isLoading, onClose, hasGroupAccess } = props;

  const { setValue } = useFormContext<ValidateOutputDataType>();
  const selectedOutputIds = useWatch<ValidateOutputDataType, 'outputIds'>({
    name: 'outputIds',
  });
  const systemClassifierItemsById = useSystemClassifierItemsBy([
    'FREQUENCY',
    'OUTPUT_TYPE',
  ]);

  if (!data) return null;

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              label={t('button.validate')}
              disabled={selectedOutputIds.length === 0}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>
            {t('title', { count: data.outputs.length })}
          </TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="small">
          <Detail label={t('group')}>
            {`${data?.groupCode} - ${data.groupName}`}
          </Detail>
          <Detail label={t('valueDate')}>{formatDate(data.valueDate)}</Detail>
          <Detail label={t('frequency')}>
            {systemClassifierItemsById['FREQUENCY'].get(data.frequencyId)?.name}
          </Detail>
        </Flex>
        <Padding top>
          <ActiveOutputsTable
            mode="edit"
            data={data}
            hasGroupAccess={hasGroupAccess}
            selectionMode="multiple"
            selectedOutputIds={selectedOutputIds}
            onSelectedOutputIdsChange={(selectedIds) =>
              setValue('outputIds', selectedIds)
            }
            isSelectable={(output) =>
              isSelectable(output, systemClassifierItemsById['OUTPUT_TYPE'])
            }
          />
        </Padding>
      </Padding>
    </Modal>
  );
};

const getDefaultValues = (
  data: OutputDescriptionProcessingStatesType,
  outputTypes: Map<any, { code: string }>,
) => {
  return {
    valueDate: data.valueDate,
    groupId: data.groupId,
    outputIds: [
      ...(data?.outputs
        .filter((output) => isSelectable(output, outputTypes))
        .map((output) => output.id) || []),
    ],
  };
};

const isSelectable = (
  output: OutputDescriptionProcessingStateType,
  outputTypes: Map<any, { code: string }>,
) =>
  outputTypes.get(output.outputTypeId)?.code !== 'INTERMEDIATE' &&
  output.activeDataObjectVersionId !== null &&
  output.processingStateCode !== 'PENDING';
